import React, { useState, useMemo, useEffect } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { Link, Outlet, useLocation } from "react-router-dom";
import "./MainContent.css";
import FloatingButton from "../Common/FloatingButton/floatingButton";
import SupportTicketDialog from "./SupportTicketDialog";
import { useSupport } from "../../context/SupportContext";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
/* ---------- ICON MAP ---------- */
const iconMap = {
  AdminPanel: <SettingsIcon fontSize="medium" />,
  BottlerForecast: <AccountTreeIcon fontSize="medium" />,
  BottlerMarketForecast: <AssuredWorkloadIcon fontSize="medium" />,
  TicketSummary: <SummarizeIcon fontSize="medium" />,
  ProfitCenter: <BusinessCenterIcon fontSize="medium" />,
  BottlerBusinessPlan: <AddBusinessOutlinedIcon fontSize="medium" />,
  FGInventory: <Inventory2OutlinedIcon fontSize="medium" />,
  Reformulation: <TrackChangesOutlinedIcon fontSize="medium" />,
  ConcentrateInventory: <InventoryOutlinedIcon fontSize="medium" />,
  ProductionRequirement: <FactoryOutlinedIcon fontSize="medium" />,
};

/* ---------- ROUTE ALIASES FOR ACTIVE STATE ---------- */
const ACTIVE_ROUTE_ALIASES = {
  "/manage-user": ["/manage-user", "/user-details"],
  "/bottler-onboarding": [
    "/bottler-onboarding",
    "/partner-config",
    "/plant-config",
    "/mf-country-config",
    "/fg-country-config",
    "/bp-country-config",
    "/ci-country-config",
    "/pr-country-config",
    "/bf-country-config"
  ],
  "/bf-manage-forecast": ["/bf-manage-forecast", "/mapping-configuration"],
  "/bmf-manage-forecast": ["/bmf-manage-forecast", "/bmf-custom-configuration"],
  "/bp-manage-forecast": ["/bp-manage-forecast", "/bp-custom-configuration"],
  "/manageConcentrateInventory": [
    "/manageConcentrateInventory",
    "/ci-custom-configuration",
  ],
  "/fg-manage-forecast": ["/fg-manage-forecast", "/fgi-custom-configuration"],
  "/manageProductionRequirement": [
    "/manageProductionRequirement",
    "/pr-custom-configuration",
  ],
};

const Sidebar = ({ menuData = [] }) => {
  const location = useLocation();
  const [openSupport, setOpenSupport] = useState(false);

  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [activeMenuCode, setActiveMenuCode] = useState(null);
  const [currentMenu, setCurrentMenu] = useState("");
  const [currentParentMenu, setCurrentParentMenu] = useState("");
  const {
    setCombinationDetails,
    setCard1CombinationDetails,
    setCard2CombinationDetails,
    setPopupCombinationDetails,
  } = useSupport();

  /* ---------- ACTIVE ROUTE CHECK ---------- */
  const isMenuItemActive = (menuPath) => {
    if (!menuPath) return false;

    const aliases = ACTIVE_ROUTE_ALIASES[menuPath];

    if (aliases) {
      return aliases.some((route) => location.pathname.startsWith(route));
    }

    return location.pathname.startsWith(menuPath);
  };

  /* ---------- CLEAN + SORT MENU DATA ---------- */
  const sortedMenus = useMemo(() => {
    return [...menuData]
      .sort((a, b) => a.menuDisplayOrder - b.menuDisplayOrder)
      .map((menu) => {
        const validSubmenus = (menu.submenuList || []).filter(
          (sub) =>
            sub?.submenuPath && sub?.submenuDisplayName && sub?.submenuCode,
        );

        const uniqueSubmenus = Array.from(
          new Map(validSubmenus.map((sub) => [sub.submenuPath, sub])).values(),
        );

        return {
          ...menu,
          submenuList: uniqueSubmenus,
        };
      });
  }, [menuData]);

  const activeMenu = sortedMenus.find(
    (menu) => menu.menuCode === activeMenuCode,
  );

  const isPathActive = (menuPath) => {
    if (!menuPath) return false;

    const aliases = ACTIVE_ROUTE_ALIASES[menuPath];

    if (aliases) {
      return aliases.some((p) => location.pathname.startsWith(p));
    }

    return location.pathname.startsWith(menuPath);
  };

  useEffect(() => {
    let foundParentMenu = null;
    let foundSubmenu = null;

    sortedMenus.forEach((menu) => {
      menu.submenuList.forEach((sub) => {
        if (isPathActive(sub.submenuPath)) {
          foundParentMenu = menu;
          foundSubmenu = sub;
        }
      });

      if (menu.menuPath && isPathActive(menu.menuPath) && !foundSubmenu) {
        foundParentMenu = menu;
        foundSubmenu = null;
      }
    });

    if (foundParentMenu) {
      const newMenu = foundSubmenu
        ? foundSubmenu.submenuDisplayName
        : foundParentMenu.menuDisplayName;

      const newParentMenu = foundParentMenu.menuDisplayName;

      setCurrentMenu((prev) => (prev !== newMenu ? newMenu : prev));
      setCurrentParentMenu((prev) =>
        prev !== newParentMenu ? newParentMenu : prev,
      );
      if (currentMenu !== newMenu) {
        setCombinationDetails([]);
        setCard1CombinationDetails([]);
        setCard2CombinationDetails([]);
        setPopupCombinationDetails([]);
      }
    }
  }, [location.pathname, sortedMenus]);
  /* ---------- MENU HANDLERS ---------- */
  const handleMenuOpen = (event, menuCode) => {
    event.stopPropagation();
    setSubmenuAnchor(event.currentTarget);
    setActiveMenuCode(menuCode);
  };

  const handleMenuClose = () => {
    setSubmenuAnchor(null);
    setActiveMenuCode(null);
  };

  return (
    <div className="main-content">
      {/* ---------- SIDEBAR ---------- */}
      <div className="sidebar">
        <List className="sideBarOptionsList" sx={{ mt: "15px" }}>
          {sortedMenus.map((menu) => {
            const hasValidSubmenu = menu.submenuList.length > 0;
            const shouldOpenSubmenu = !menu.menuPath && hasValidSubmenu;

            const isMenuActive =
              isMenuItemActive(menu.menuPath) ||
              menu.submenuList.some((sub) => isMenuItemActive(sub.submenuPath));

            return (
              <ListItem key={menu.menuCode} disablePadding>
                <ListItemButton
                  component={shouldOpenSubmenu ? "div" : Link}
                  to={shouldOpenSubmenu ? undefined : menu.menuPath}
                  onClick={(e) =>
                    shouldOpenSubmenu
                      ? handleMenuOpen(e, menu.menuCode)
                      : handleMenuClose()
                  }
                  sx={{
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    boxSizing: "border-box",
                    border: "1px solid transparent",
                    borderColor: isMenuActive
                      ? "rgba(128,128,128,0.45)"
                      : "transparent",
                    backgroundColor: isMenuActive ? "#fff" : "",
                    "&:hover": {
                      backgroundColor: isMenuActive
                        ? "#fff"
                        : "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      "& svg": {
                        color: isMenuActive ? "#f40000" : "#fff",
                      },
                    }}
                  >
                    {iconMap[menu.menuCode] || null}
                  </ListItemIcon>

                  <ListItemText
                    primary={menu.menuDisplayName}
                    primaryTypographyProps={{
                      fontSize: "12px",
                      fontWeight: 450,
                      color: isMenuActive ? "#f40000" : "#fff",
                      textAlign: "center",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </div>

      {/* ---------- SUBMENU POPPER ---------- */}
      <Popper
        open={Boolean(submenuAnchor)}
        anchorEl={submenuAnchor}
        placement="right-start"
        modifiers={[
          {
            name: "offset",
            options: { offset: [6, 4] },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleMenuClose}>
          <Paper
            elevation={6}
            sx={{
              minWidth: 220,
              borderRadius: "6px",
              overflow: "hidden",
              border: "1px solid black",
              backgroundColor: "#fff",
            }}
          >
            <List dense>
              {(activeMenu?.submenuList || [])
                .sort((a, b) => a.submenuDisplayOrder - b.submenuDisplayOrder)
                .map((sub) => {
                  const isSubActive = isMenuItemActive(sub.submenuPath);

                  return (
                    <ListItemButton
                      key={sub.submenuCode}
                      component={Link}
                      to={sub.submenuPath}
                      onClick={handleMenuClose}
                      sx={{
                        backgroundColor: isSubActive
                          ? "#f5f5f5"
                          : "transparent",
                        color: isSubActive ? "#f40000" : "black",
                        "&:hover": {
                          backgroundColor: "#D3D3D3",
                        },
                      }}
                    >
                      <ListItemText
                        primary={sub.submenuDisplayName}
                        primaryTypographyProps={{
                          fontSize: "12px",
                          fontWeight: isSubActive ? 450 : 400,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* ---------- MAIN CONTENT ---------- */}
      <div className="content">
        <div>
          <FloatingButton
            label="SUPPORT"
            onClick={() => {
              setOpenSupport(true);
            }}
            disabled={location.pathname === "/ticket-summary"}
          />
          <Outlet />
        </div>
      </div>
      <SupportTicketDialog
        currentMenu={currentMenu}
        currentParentMenu={currentParentMenu}
        open={openSupport}
        onClose={() => {
          setOpenSupport(false);
        }}
      ></SupportTicketDialog>
    </div>
  );
};

export default Sidebar;
